import { t } from '../i18n/translate';

// Mobile Detection Utility for SAM

/**
 * Check if the current device is mobile
 */
export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         Boolean(navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
};

/**
 * Check if the current device is iOS
 */
export const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/**
 * Check if the current device is Android
 */
export const isAndroid = (): boolean => {
  return /Android/.test(navigator.userAgent);
};

/**
 * Check if the current device is a tablet
 */
export const isTablet = (): boolean => {
  return /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)/.test(navigator.userAgent) ||
         (window.innerWidth >= 768 && window.innerWidth <= 1024);
};

/**
 * Check if the current device is a phone
 */
export const isPhone = (): boolean => {
  return isMobile() && !isTablet();
};

/**
 * Check if the browser supports File System Access API
 */
export const supportsFileSystemAPI = (): boolean => {
  const hasFileSystemAPI = 'showSaveFilePicker' in window && 'showOpenFilePicker' in window;
  console.log('🔧 File System API check:', {
    hasShowSaveFilePicker: 'showSaveFilePicker' in window,
    hasShowOpenFilePicker: 'showOpenFilePicker' in window,
    result: hasFileSystemAPI
  });
  return hasFileSystemAPI;
};

/**
 * Check if the browser supports Web Share API
 */
export const supportsWebShareAPI = (): boolean => {
  return 'share' in navigator;
};

/**
 * Check if the browser supports PWA features
 */
export const supportsPWA = (): boolean => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Get the best export method for the current device
 */
export const getBestExportMethod = (): 'native-share' | 'file-system' | 'download' => {
  const mobile = isMobile();
  const hasFileSystem = supportsFileSystemAPI();
  const hasWebShare = supportsWebShareAPI();
  
  console.log('🔧 Export method selection:', {
    isMobile: mobile,
    hasFileSystemAPI: hasFileSystem,
    hasWebShareAPI: hasWebShare
  });
  
  if (mobile && hasWebShare) {
    console.log('🔧 Selected: native-share (mobile with Web Share API)');
    return 'native-share';
  }
  if (hasFileSystem) {
    console.log('🔧 Selected: file-system (desktop with File System API)');
    return 'file-system';
  }
  console.log('🔧 Selected: download (fallback)');
  return 'download';
};

/**
 * Get the best import method for the current device
 */
export const getBestImportMethod = (): 'file-system' | 'file-input' => {
  if (supportsFileSystemAPI()) {
    return 'file-system';
  }
  return 'file-input';
};

/**
 * Get device-specific tips for file operations
 */
export const getFileOperationTips = (): string[] => {
  const tips: string[] = [];
  
  if (isMobile()) {
    tips.push(`📱 ${t('backup.tips.mobile_file_manager')}`);
    tips.push(`📥 ${t('backup.tips.mobile_file_picker')}`);
    
    if (isIOS()) {
      tips.push(`🍎 ${t('backup.tips.ios')}`);
    } else if (isAndroid()) {
      tips.push(`🤖 ${t('backup.tips.android')}`);
    }
  } else {
    tips.push(`💻 ${t('backup.tips.desktop_downloads')}`);
    tips.push(`📁 ${t('backup.tips.desktop_location')}`);
  }
  
  return tips;
};

/**
 * Get device-specific backup button text
 */
export const getExportButtonText = (): string => {
  if (isMobile()) {
    if (supportsWebShareAPI()) {
      return `📤 ${t('backup.button.backup_share')}`;
    }
    return `📤 ${t('backup.button.backup')}`;
  }
  return t('backup.button.backup');
};

/**
 * Get device-specific restore button text
 */
export const getImportButtonText = (): string => {
  if (isMobile()) {
    return `📥 ${t('backup.button.choose_file_restore')}`;
  }
  return t('backup.button.choose_file');
};

/**
 * Check if the app is running as a PWA
 */
export const isPWAInstalled = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
};

/**
 * Get device capabilities summary
 */
export const getDeviceCapabilities = () => {
  return {
    isMobile: isMobile(),
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isTablet: isTablet(),
    isPhone: isPhone(),
    supportsFileSystemAPI: supportsFileSystemAPI(),
    supportsWebShareAPI: supportsWebShareAPI(),
    supportsPWA: supportsPWA(),
    isPWAInstalled: isPWAInstalled(),
    bestExportMethod: getBestExportMethod(),
    bestImportMethod: getBestImportMethod()
  };
}; 